import { useEffect, useState } from 'react';

const SelectClass = ({
  setDisable,
  classData,
  setClassData,
  courses = [],
  batchStructures = [],
  fetchBatchStructures,
  fetchBatchesByStructure,
  selectedBranch,
  loadingCourses,
  clearData,
}) => {
  const [errors, setErrors] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);

  /* ================= COURSE CHANGE ================= */
  const handleCourseChange = async (e) => {
    const courseId = e.target.value;

    setClassData({
      courseId,
      batchStructureId: '',
      batchId: '',
    });

    setBatches([]);
    clearData();

    if (courseId) {
      await fetchBatchStructures(courseId);
    }
  };

  /* ================= STRUCTURE CHANGE ================= */
  const handleStructureChange = async (e) => {
    const batchStructureId = e.target.value;

    setClassData((prev) => ({
      ...prev,
      batchStructureId,
      batchId: '',
    }));

    setBatches([]);
    setLoadingBatches(true);
    clearData();

    if (batchStructureId) {
      try {
        const res = await fetchBatchesByStructure(
          batchStructureId,
          selectedBranch
        );
        setBatches(Array.isArray(res) ? res : []);
      } finally {
        setLoadingBatches(false);
      }
    } else {
      setLoadingBatches(false);
    }
  };

  /* ================= BATCH CHANGE ================= */
  const handleBatchChange = (e) => {
    const batchId = e.target.value;
    setClassData((prev) => ({ ...prev, batchId }));
    clearData();
  };

  /* ================= VALIDATION ================= */
  useEffect(() => {
    const invalid =
      !classData.courseId || !classData.batchStructureId || !classData.batchId;

    setErrors(invalid ? ['Please fill in all fields.'] : []);
    setDisable(invalid);
  }, [classData, setDisable]);

  return (
    <div className="w-full rounded-lg px-3 pt-7 md:px-8">
      <div className="flex justify-center">
        <h4 className="text-lg font-bold text-navy-700 dark:text-white">
          Select Class Information
        </h4>
      </div>

      <div className="mt-6 flex flex-col md:flex-row md:gap-6">
        {/* ================= COURSE ================= */}
        <div className="w-full md:w-1/3">
          <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
            Select Course
          </label>

          <select
            onChange={handleCourseChange}
            value={classData.courseId}
            className="mt-2 w-full rounded-xl border px-3 py-3 text-sm outline-none dark:bg-navy-800 dark:text-white"
          >
            <option value="">Click here to Select Course</option>

            {loadingCourses ? (
              <option>Loading courses...</option>
            ) : Array.isArray(courses) && courses.length > 0 ? (
              courses.map((course) => (
                <option key={course._id} value={course._id}>
                  {course.courseName}
                </option>
              ))
            ) : (
              <option disabled>No courses available</option>
            )}
          </select>
        </div>

        {/* ================= BATCH STRUCTURE ================= */}
        <div className="w-full md:w-1/3">
          <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
            Select Batch Structure
          </label>

          <select
            onChange={handleStructureChange}
            value={classData.batchStructureId}
            disabled={!classData.courseId}
            className="mt-2 w-full rounded-xl border px-3 py-3 text-sm outline-none disabled:opacity-60 dark:bg-navy-800 dark:text-white"
          >
            <option value="">Click here to Select Structure</option>

            {Array.isArray(batchStructures) && batchStructures.length > 0 ? (
              batchStructures.map((bs) => (
                <option key={bs._id} value={bs._id}>
                  {bs.name}
                </option>
              ))
            ) : (
              <option disabled>No structures available</option>
            )}
          </select>
        </div>

        {/* ================= BATCH (AS YOU WANTED 🔥) ================= */}
        <div className="w-full md:w-1/3">
          <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
            Select Batch
          </label>

          <select
            onChange={handleBatchChange}
            value={classData.batchId}
            disabled={!classData.batchStructureId}
            className="mt-2 w-full rounded-xl border px-3 py-3 text-sm outline-none disabled:opacity-60 dark:bg-navy-800 dark:text-white"
          >
            <option value="">
              {loadingBatches
                ? 'Loading batches...'
                : 'Click here to Select Batch'}
            </option>

            {!loadingBatches && batches.length === 0 && (
              <option disabled>No batches available</option>
            )}

            {!loadingBatches &&
              Array.isArray(batches) &&
              batches.map((batch) => {
                const times = Array.isArray(batch.daySchedules)
                  ? batch.daySchedules.map((d) => d.startTime).filter(Boolean)
                  : [];

                const uniqueTimes = [...new Set(times)];
                const timeDisplay =
                  uniqueTimes.length > 0
                    ? ` at ${uniqueTimes.join(' / ')}`
                    : '';

                return (
                  <option key={batch._id} value={batch._id}>
                    B {batch.batchNumber} – From{' '}
                    {new Date(batch.startDate).toLocaleDateString()} to{' '}
                    {new Date(batch.endDate).toLocaleDateString()}
                    {timeDisplay}
                  </option>
                );
              })}
          </select>
        </div>
      </div>

      {errors.length > 0 && (
        <div className="mt-4 text-red-500">
          {errors.map((e, i) => (
            <p key={i}>{e}</p>
          ))}
        </div>
      )}
    </div>
  );
};

export default SelectClass;
